import React from "react";
import api from "../lib/api";
import { SUPPORTED, SUPPORTED_CODES, detectCurrency, formatMoney } from "../lib/currency";

const CurrencyContext = React.createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = React.useState("USD");
  const [rates, setRates] = React.useState({ USD: 1 });
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    setCurrencyState(detectCurrency());
    api.get("/shop/rates")
      .then((r) => { if (r.data?.rates) setRates(r.data.rates); if (r.data?.updated_at) setUpdatedAt(r.data.updated_at); })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const setCurrency = React.useCallback((code) => {
    setCurrencyState(code);
    try { localStorage.setItem("shop_currency", code); } catch (e) { /* ignore */ }
  }, []);

  const convert = React.useCallback((usd) => {
    const rate = rates[currency] || 1;
    return (Number(usd) || 0) * rate;
  }, [rates, currency]);

  const format = React.useCallback((usd) => formatMoney(convert(usd), currency), [convert, currency]);

  const ratesUpdatedText = React.useMemo(() => {
    if (!updatedAt) return "";
    const d = new Date(updatedAt);
    if (isNaN(d)) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yst = new Date(now); yst.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yst.toDateString();
    if (sameDay) return "Rates updated today";
    if (isYesterday) return "Rates updated yesterday";
    return `Rates updated ${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  }, [updatedAt]);

  const value = {
    currency, setCurrency, rates, ready,
    convert, format, updatedAt, ratesUpdatedText,
    supported: SUPPORTED.filter((c) => c.code === "USD" || rates[c.code]),
    isConverted: currency !== "USD",
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => {
  const ctx = React.useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback if used outside provider
    return {
      currency: "USD", setCurrency: () => {}, rates: { USD: 1 }, ready: true,
      convert: (u) => Number(u) || 0, format: (u) => `$${(Number(u) || 0).toFixed(2)}`,
      supported: SUPPORTED, isConverted: false, updatedAt: null, ratesUpdatedText: "",
    };
  }
  return ctx;
};
